import React, { useState, Suspense, FC, MouseEvent, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { rudderanalytics } from '../../rudderanalytics';
import EnvelopeIcon from './../../images/contact/envelope-icon.svg';
import {
  ContactStyled,
  LeftColumn,
  RightColumn,
  Title,
  Text,
  TextFeatured,
  Mail,
  ContactForm,
  FormTitle,
  InputContainer,
  Btn,
  LoaderContainer,
} from './style';

interface Props {}

type FormValuesType = {
  name: string;
  company: string;
  email: string;
  message: string;
};

const Contact: FC<Props> = () => {
  const [formValues, setFormValues] = useState<FormValuesType>({
    name: '',
    company: '',
    email: '',
    message: '',
  });
  const [nameError, setNameError] = useState(false);
  const [companyError, setCompanyError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [messageError, setMessageError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDefaultError, setShowDefaultError] = useState(false);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [captcha, setCaptcha] = useState(false);

  const recaptchaRef = React.createRef();
  const { t } = useTranslation();

  const setToggleInput = (e: MouseEvent) => {
    const el: HTMLElement | null = e.currentTarget.querySelector('label');
    const labels: HTMLCollectionOf<HTMLLabelElement> =
      document.getElementsByTagName('label');
    Array.from(labels).map((item) => item.classList.remove('active-input'));
    if (el) {
      el.classList.toggle('active-input');
    }
  };

  const handleInputChange = (e: React.ChangeEvent) => {
    e.persist();
    const { name, value } = e.target as HTMLInputElement;
    setFormValues({ ...formValues, [name]: value });
  };

  const intentionWasCaptured = useRef(false);
  const handleInputFocus = () => {
    if (intentionWasCaptured.current) return;
    rudderanalytics.track('contact_form_intention');
    intentionWasCaptured.current = true;
  };

  const validateInputs = () => {
    const emailPattern =
      /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@(([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    let nameError = formValues.name.trim() === '';
    let companyError = formValues.company.trim() === '';
    let emailError =
      formValues.email.trim() === '' ||
      !emailPattern.test(formValues.email.trim());
    let messageError = formValues.message.trim() === '';

    setNameError(nameError);
    setCompanyError(companyError);
    setEmailError(emailError);
    setMessageError(messageError);

    return !nameError && !companyError && !emailError && !messageError;
  };

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    if (!validateInputs() || !captcha) {
      if (!captcha) setShowDefaultError(true);
      setSubmitting(false);
      return;
    } else {
      try {
        setShowDefaultError(false);
        setShowSuccessMsg(false);
        rudderanalytics.identify(formValues.email, {
          email: formValues.email,
        });
        rudderanalytics.track('contact_form_submit', { ...formValues });
        fetch(
          'https://wc13zhw8z5.execute-api.us-east-1.amazonaws.com/default/sendContactEmail',
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            method: 'POST',
            body: JSON.stringify(formValues),
          }
        )
          .then((res) => res.json())
          .then((res) => {
            setFormValues({
              name: '',
              company: '',
              email: '',
              message: '',
            });
            setSubmitting(false);
            setNameError(false);
            setCompanyError(false);
            setEmailError(false);
            setMessageError(false);
            setShowDefaultError(false);
            setShowSuccessMsg(true);
          })
          .catch((res) => {
            setSubmitting(false);
            setShowDefaultError(true);
            setShowSuccessMsg(false);
            throw res;
          });
      } catch (e) {
        console.error(e);
        setShowDefaultError(true);
      }
    }
  };

  return (
    <Suspense fallback={<div />}>
      <Helmet>
        <meta
          name="description"
          content="Do you want to learn how to apply Machine Learning to make the best of use of your data? Contact us!"
        />
        <title>Mutt Data | Contact Us!</title>
        <meta property="og:title" content="Mutt Data | Contact Us!" />
        <meta
          property="og:description"
          content="Do you want to learn how to apply Machine Learning to make the best of use of your data? Contact us!"
        />
        <meta
          property="og:image"
          content={`https://muttdata.ai/email/logo.png`}
        />
        <meta
          name="keywords"
          content="
			machine learning
			big data
			contact us
			"
        />
      </Helmet>

      <ContactStyled>
        <div className="container">
          <LeftColumn>
            <Title
              className="animate__animated animate__fadeIn"
              dangerouslySetInnerHTML={{
                __html: t('ContactTitle'),
              }}
            />
            <Text
              dangerouslySetInnerHTML={{
                __html: t('ContactText'),
              }}
            />
            <TextFeatured>{t('ContactTextFeatured')}</TextFeatured>
            <Mail>
              <div>
                <strong>{t('ContactDropLine')}</strong>
                <a
                  href="mailto:hi@muttdata.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  hi@muttdata.ai
                </a>
              </div>
              <img src={EnvelopeIcon} alt="icon" />
            </Mail>
          </LeftColumn>
          <RightColumn
            className={
              nameError || companyError || emailError || messageError
                ? 'has-errors'
                : ''
            }
          >
            <FormTitle>{t('ContactFormTitle')}</FormTitle>
            <ContactForm
              onSubmit={(e) => submitHandler(e)}
              className={
                nameError || companyError || emailError || messageError
                  ? 'has-errors'
                  : ''
              }
              hasDefaultError={showDefaultError}
              hasSuccessMsg={showSuccessMsg}
            >
              <InputContainer
                onClick={(e) => setToggleInput(e)}
                hasError={nameError}
              >
                <label>
                  {t('ContactFormName')}
                  <input
                    type="text"
                    value={formValues.name}
                    name="name"
                    tabIndex={1}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                  />
                  <span className="error">{t('ContactFormNameError')}</span>
                </label>
              </InputContainer>
              <InputContainer
                onClick={(e) => setToggleInput(e)}
                hasError={companyError}
              >
                <label>
                  {t('ContactFormCompany')}
                  <input
                    type="text"
                    value={formValues.company}
                    name="company"
                    tabIndex={2}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                  />
                  <span className="error">{t('ContactFormCompanyError')}</span>
                </label>
              </InputContainer>
              <InputContainer
                onClick={(e) => setToggleInput(e)}
                hasError={emailError}
              >
                <label>
                  {t('ContactFormEmail')}
                  <input
                    type="text"
                    value={formValues.email}
                    name="email"
                    tabIndex={3}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                  />
                  <span className="error">{t('ContactFormEmailError')}</span>
                </label>
              </InputContainer>
              <InputContainer
                onClick={(e) => setToggleInput(e)}
                hasError={messageError}
              >
                <label>
                  {t('ContactFormMessage')}
                  <textarea
                    name="message"
                    value={formValues.message}
                    tabIndex={4}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                  ></textarea>
                  <span className="error">{t('ContactFormMessageError')}</span>
                </label>
              </InputContainer>
              <div className="btn-container">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6LcI--wZAAAAAF9Zbhc0ZtnehoUp7gUOx5Nw91fi"
                  onChange={(captcha: boolean) => {
                    if (captcha) {
                      setCaptcha(true);
                    } else {
                      setCaptcha(false);
                    }
                  }}
                  onExpired={() => {
                    setCaptcha(false);
                  }}
                  onErrored={() => {
                    setCaptcha(false);
                  }}
                />
                <Btn isSubmitting={submitting}>
                  <LoaderContainer>
                    <div className="text">{t('ContactFormButton')}</div>
                    <div className="spin"></div>
                  </LoaderContainer>
                </Btn>
              </div>
              <div className="msg-continer">
                <div
                  className="msg-error"
                  dangerouslySetInnerHTML={{
                    __html: t('ContactFormDefaultError'),
                  }}
                />
                <div
                  className="msg-success"
                  dangerouslySetInnerHTML={{
                    __html: t('ContactFormSuccessMsg'),
                  }}
                />
              </div>
            </ContactForm>
          </RightColumn>
          <Mail>
            <div>
              <strong>{t('ContactDropLine')}</strong>
              <a
                href="mailto:hi@muttdata.ai"
                target="_blank"
                rel="noopener noreferrer"
              >
                hi@muttdata.ai
              </a>
            </div>
            <img src={EnvelopeIcon} alt="icon" />
          </Mail>
        </div>
      </ContactStyled>
    </Suspense>
  );
};

export default Contact;
